// Cave84 — Wine Analysis hooks (Phase 2 Week 7)

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult
} from '@tanstack/react-query';
import type { AnalysisStatus, GenerationQueueEntry } from '@cave84/shared-types';
import {
  listPendingWineAnalysisJobs,
  queueWineAnalysis,
  saveOwnerOverride,
  clearOwnerOverridesAndQueue,
  type QueuePriority
} from '@/services/wineAnalysis';
import type { WineAnalysis } from '@cave84/shared-types';
import { useWine } from '@/hooks/useWines';
import { qk } from '@/services/firestore';

// ---------------------------------------------------------------------------
// Read — analysis lives on Wine doc
// ---------------------------------------------------------------------------

/**
 * Returns the WineAnalysis subdoc from the Wine document.
 * Re-uses useWine so no extra Firestore read is needed.
 */
export function useWineAnalysis(wineId: string | undefined): UseQueryResult<WineAnalysis | null> {
  const wineQuery = useWine(wineId);
  return {
    ...wineQuery,
    data: wineQuery.data?.analysis ?? null,
  } as UseQueryResult<WineAnalysis | null>;
}

// ---------------------------------------------------------------------------
// Queue regeneration
// ---------------------------------------------------------------------------

interface QueueInput { wineId: string; priority?: QueuePriority }

export function useQueueWineAnalysis(): UseMutationResult<string, Error, QueueInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ wineId, priority }: QueueInput) =>
      queueWineAnalysis(wineId, priority ?? 'normal'),
    onSuccess: (_id, { wineId }) => {
      void qc.invalidateQueries({ queryKey: qk.wines.byId(wineId) });
      void qc.invalidateQueries({ queryKey: ['generation_queue'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Save owner override for one section
// ---------------------------------------------------------------------------

type SectionKey = keyof Pick<WineAnalysis, 'producer_note' | 'terroir_note'>;

interface OverrideInput { wineId: string; sectionKey: SectionKey; content: string }

export function useSaveOwnerOverride(): UseMutationResult<void, Error, OverrideInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ wineId, sectionKey, content }: OverrideInput) =>
      saveOwnerOverride(wineId, sectionKey, content),
    onSuccess: (_v, { wineId }) => {
      void qc.invalidateQueries({ queryKey: qk.wines.byId(wineId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Clear owner overrides + queue for full regeneration (Regenerate from scratch)
// Spec §1551: owner_overrides cleared, status reset to pending, fresh queue entry
// ---------------------------------------------------------------------------

interface ClearOverridesInput { wineId: string; priority?: QueuePriority }

export function useClearOwnerOverrides(): UseMutationResult<string, Error, ClearOverridesInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ wineId, priority }: ClearOverridesInput) =>
      clearOwnerOverridesAndQueue(wineId, priority ?? 'high'),
    onSuccess: (_id, { wineId }) => {
      void qc.invalidateQueries({ queryKey: qk.wines.byId(wineId) });
      void qc.invalidateQueries({ queryKey: ['generation_queue'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Pending jobs (dashboard widget)
// ---------------------------------------------------------------------------

export function usePendingAnalysisJobs(): UseQueryResult<GenerationQueueEntry[]> {
  return useQuery({
    queryKey: ['generation_queue', 'pending', 'wine_analysis'],
    queryFn: listPendingWineAnalysisJobs,
    staleTime: 30_000,
    refetchInterval: 60_000, // poll every minute while page is open
  });
}

// Re-export AnalysisStatus for convenience in components
export type { AnalysisStatus };
